import angular, { IOnDestroy, IOnInit, isFunction } from "angular";
import { UserProfile } from "../../application/UserProfile";
import {
  CsvColumnConfig,
  CsvConfig,
  CsvWriter,
  vsName as CsvWriterName,
} from "../csv/csvWriter";
import { Record } from "../server/crud";
import { IQuery } from "../server/query";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../server/serverUtility";
import { ValueGetter } from "./cells/valueGetters";
import { ColDef } from "./columns/colDef";
import { RecordDataSource } from "./recordDataSource";
import { IGridSettings } from "./settings/gridSettingsService";

export interface GridSelectionChangedArgs {
  data: Record;
  gridSelection: number[];
  selected: boolean;
}

export interface IRowCommandEventArgs<TRow> {
  commandName: string;
  data: TRow;
  gridApi: awk.grid.GridApi;
}

export class RecordGridController implements IOnInit, IOnDestroy {
  static $inject = ["$scope", CsvWriterName, ServerUtilityName, "userProfile"];

  //bindings
  columns!: ColDef[];
  datasource!: RecordDataSource;
  gridSettings!: IGridSettings;
  defaultQuery: IQuery = {};
  defaultSort: string | undefined;
  gridSelectionChanged: (args: GridSelectionChangedArgs) => void = () => {};
  onItemCommand: (a: { args: IRowCommandEventArgs<unknown> }) => void =
    () => {};
  initialSelectedIds: number[] | undefined;
  showSelectButtons: boolean = false;
  showDownloadButton: boolean = true;

  rowIdSelector: (locals: { row: any }) => number =
    RecordGridController.defaultRowIdSelector;

  static defaultRowIdSelector = (locals: any) => locals.row.Id;

  currentQuery: IQuery = {};

  private selectedRowIds: number[] = [];

  private readonly gridOptions: awk.grid.GridOptions = {
    enableServerSideSorting: true,
    enableServerSideFilter: true,
    enableColResize: true,
    angularCompileRows: true,
    angularCompileFilters: true,
    showToolPanel: false,
    toolPanelSuppressValues: true,
    toolPanelSuppressPivot: true,
    ready: () => this.gridReady(),
    rowHeight: 40,
    afterSortChanged: () => this.sortChanged(),
  };
  private queryUnsub: (() => void) | undefined;
  private defaultQueryUnsub: (() => void) | undefined;
  private commandUnsub: (() => void) | undefined;

  constructor(
    private readonly $scope: angular.IScope,
    private readonly csvWriter: CsvWriter,
    private readonly serverUtility: ServerUtility,
    private readonly userProfile: UserProfile
  ) {}

  $onDestroy() {
    this.queryUnsub?.call(undefined);
    this.defaultQueryUnsub?.call(undefined);
    this.commandUnsub?.call(undefined);
  }
  $onInit() {
    const initialSelectedIds = this.initialSelectedIds ?? [];
    this.selectedRowIds = [...initialSelectedIds];
    this.gridOptions.columnDefs = this.columns.filter((c) => {
      if (!c.requiresPermission) {
        return true;
      }

      if (Array.isArray(c.requiresPermission)) {
        const permissions = <string[]>c.requiresPermission;
        return permissions.reduce<boolean>(
          (result, next) => result && this.userProfile.hasPermission(next),
          true
        );
      }

      const str = <string>c.requiresPermission;
      return this.userProfile.hasPermission(str);
    });
    this.datasource.getOrderByField = (() => this.getOrderByString()).bind(
      this
    );
    const savedQuery = this.gridSettings.getQuery();
    this.currentQuery = { ...savedQuery };

    this.queryUnsub = this.$scope.$watchCollection(
      () => this.currentQuery,
      () => this.queryChanged()
    );

    this.defaultQueryUnsub = this.$scope.$watchCollection(
      () => this.defaultQuery,
      () => this.queryChanged()
    );

    this.commandUnsub = this.$scope.$on(
      "command",
      (e, args: IRowCommandEventArgs<any>) => {
        e.preventDefault();
        this.itemCommand(args.commandName, args.data);
      }
    );
  }

  selectAllOnPage(select: boolean) {
    const pageRecords =
      this.gridOptions.api?.getRenderedNodes().map((node) => node.data) ?? [];

    const newSelections = pageRecords.filter(
      (r) => this.rowSelected(r) === !select
    );
    newSelections.forEach((r) => {
      this.toggleRowSelection(r);
    });
  }

  rowSelected(row: Record): boolean {
    const rowId = this.rowIdSelector({ row });
    return this.selectedRowIds?.includes(rowId) ?? false;
  }

  toggleRowSelection(row: Record) {
    const rowId = this.rowIdSelector({ row });
    const collectionIndex = this.selectedRowIds.indexOf(rowId);
    const newSelection = collectionIndex < 0;
    if (newSelection) {
      this.selectedRowIds.push(rowId);
    } else {
      this.selectedRowIds.splice(collectionIndex, 1);
    }

    this.gridSelectionChanged({
      data: row,
      gridSelection: [...this.selectedRowIds],
      selected: newSelection,
    });
  }

  private queryChanged(): void {
    this.gridSettings.saveQuery(this.currentQuery);

    const newQuery = this.getQuery(this.currentQuery);
    this.datasource.query = newQuery;
    this.gridOptions.api?.onNewDatasource();
  }

  private sortChanged() {
    const sort = this.gridOptions.api!.getSortModel();
    this.gridSettings.saveSort(sort);
  }

  private getQuery(query: IQuery) {
    return { ...query, ...this.defaultQuery };
  }

  private getOrderByString(): string | undefined {
    const sortModel = this.gridOptions.api?.getSortModel();

    if (!sortModel || sortModel.length === 0) {
      return this.defaultSort;
    }

    const orderFields = sortModel.map((orderField: any) => {
      const { colId, sort: direction } = orderField;
      const sortAscending = !direction || direction !== "desc";
      const column = this.gridOptions.api?.getColumnDef(colId);
      const sortExpression = column?.field ?? colId;
      return sortAscending ? sortExpression : "-" + sortExpression;
    });
    const orderByString = orderFields.reduce(
      (result: string, field: string) => result + `${field}`
    );
    return this.defaultSort === undefined || this.defaultSort === ""
      ? orderByString
      : `${orderByString},${this.defaultSort}`;
  }

  private itemCommand(commandName: string, data: Record) {
    if (!this.onItemCommand) return;
    this.onItemCommand({
      args: {
        commandName,
        data,
        gridApi: this.gridOptions.api!,
      },
    });
  }

  clearFilters() {
    this.currentQuery = {};
  }

  toggleGridSettings() {
    const gridApi = this.gridOptions.api;
    if (!gridApi) {
      return;
    }
    const isToolPanelShown = gridApi.isToolPanelShowing();
    gridApi.showToolPanel(!isToolPanelShown);
  }

  private gridReady() {
    const columnApi = this.gridOptions.columnApi;
    const sortModel = this.gridSettings.getSort();
    if (columnApi) {
      this.gridSettings.syncColumns(columnApi);
      columnApi.addChangeListener(() => {
        this.gridSettings.saveState(columnApi);
      });
    }

    if (sortModel) {
      const sortModelWithExistingColumns = sortModel.filter((f: any) =>
        columnApi?.getAllColumns().find((c) => c.colId === f.colId)
      );
      this.gridOptions.api!.setSortModel(sortModelWithExistingColumns);
    }

    this.gridOptions.api?.setDatasource(this.datasource);
  }

  downloadCsv() {
    const columns = this.getCsvColumns();
    const query = this.getQuery(this.currentQuery);
    query.OrderBy = this.getOrderByString();
    query.Skip = 0;

    const request = { url: this.datasource.url, query };
    const serverPromise = this.datasource.useWrappedQuery
      ? this.serverUtility.fetchAllWrapped(request)
      : this.serverUtility.fetchAll(request);

    serverPromise.then((rows) => {
      const csvConfig: CsvConfig = {
        columns,
        includeHeaders: true,
      };
      const csv = this.csvWriter.getCsv(csvConfig, rows);
      this.csvWriter.saveFile({ content: csv, filename: "out.csv" });
    });
  }

  getCsvColumns(): CsvColumnConfig[] {
    const gridApi = this.gridOptions.api;
    if (!gridApi) {
      throw new Error("Could not get grid Api to build Csv column configs");
    }
    const columns = this.gridOptions.columnApi?.getState() ?? [];
    const colDefs = columns
      .filter((c) => !c.hide)
      .map((c) => gridApi.getColumnDef(c.colId));
    return colDefs
      .filter((c) => c.field)
      .map((c) => this.getCsvColumnFromGridColDef(c));
  }

  getCsvColumnFromGridColDef(colDef: ColDef): CsvColumnConfig {
    return {
      field: colDef.field!,
      header: colDef.headerName,
      formatter: (params) => this.getFormattedValue(params.row, colDef),
    };
  }

  private getFormattedValue(row: any, colDef: ColDef): string | undefined {
    if (isFunction(colDef.valueGetter)) {
      const value = (<ValueGetter>colDef.valueGetter)({ data: row, colDef });
      return (<any>value)?.toString();
    }
    return row[colDef.field!]?.toString();
  }
}
